/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      Minimal drop-in replacement for the `currency.js` npm package
 *              (integer-cents arithmetic: value / add / format), used where
 *              Embroider's AMD/resolver bridge can't resolve the `.js`-suffixed
 *              `currency.js` package name (e.g. from Ember Data models).
 *   extends:   -
 *   template:  none
 *   args:      -
 *   uses:      (none — no imports)
 *   siblings:  -
 *   tests:     currency-core-test.js (unit)
 *
 * @module beebee-base/lib/currency-core
 * @description Minimal drop-in replacement for the `currency.js` npm package,
 * covering exactly the API surface used in this codebase:
 *
 *   currency(value, { decimal, separator, symbol }).value   → number
 *   currency(...).add(other)                                → Currency
 *   currency(...).format()                                  → string
 *
 * Why not the npm package: resolver-loaded addon modules (models, helpers) stay
 * in Ember's AMD loader under Embroider, and its compat bridge cannot resolve
 * the `.js`-suffixed package name — `import currency from 'currency.js'` throws
 * "Could not find module `currency.js`" at runtime (e.g. modelFor('payment')).
 * Plain addon code like this module is compiled by every build pipeline.
 *
 * Same semantics as currency.js for the used surface: integer-cents arithmetic
 * (no floating point drift), invalid characters stripped on parse, fixed
 * precision of 2.
 */

const DEFAULTS = {
  symbol: '',
  separator: ',',
  decimal: '.'
};
function escapeRegex(char) {
  return char.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Converts a value to integer cents.
 * Strings: every character except digits, minus sign and the configured decimal
 * separator is stripped (thousands separators included), then the decimal
 * separator is normalized to '.' — mirroring currency.js parsing.
 * @param {number|string|Currency} value
 * @param {Object} settings - { decimal }
 * @returns {number} integer cents
 */
function toCents(value, settings) {
  if (value instanceof Currency) {
    return value.intValue;
  }
  if (typeof value === 'number') {
    return Math.round(value * 100);
  }
  if (typeof value === 'string') {
    const cleaned = value.replace(new RegExp(`[^\\d\\-${escapeRegex(settings.decimal)}]`, 'g'), '').replace(settings.decimal, '.');
    const parsed = parseFloat(cleaned);
    return Math.round((isNaN(parsed) ? 0 : parsed) * 100);
  }
  return 0;
}
class Currency {
  constructor(value, settings) {
    this.settings = settings;
    this.intValue = toCents(value, settings);
  }

  /** Numeric value (e.g. 1234.56) */
  get value() {
    return this.intValue / 100;
  }

  /**
   * Returns a new Currency with the sum of this and `other`
   * (number, string or Currency), keeping this instance's settings.
   */
  add(other) {
    const result = new Currency(0, this.settings);
    result.intValue = this.intValue + toCents(other, this.settings);
    return result;
  }

  /**
   * Formats using the configured separator/decimal/symbol,
   * e.g. { separator: '.', decimal: ',' } → "1.234,56".
   */
  format() {
    const {
      separator,
      decimal,
      symbol
    } = this.settings;
    const negative = this.intValue < 0;
    const abs = Math.abs(this.intValue);
    const units = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, separator);
    const cents = String(abs % 100).padStart(2, '0');
    return `${negative ? '-' : ''}${symbol}${units}${decimal}${cents}`;
  }
}

/**
 * Factory with the same call signature as the `currency.js` default export.
 * @param {number|string|Currency} value
 * @param {Object} [opts] - { decimal, separator, symbol }
 * @returns {Currency}
 */
function currency(value, opts = {}) {
  return new Currency(value, {
    ...DEFAULTS,
    ...opts
  });
}

export { currency as default };
//# sourceMappingURL=currency-core.js.map
